import React from "react";
import { ProductForm, type ProductFormData } from "@semo/shared-product";
import { toast } from "sonner";
import { useSupplierRequests } from "../items/application/hooks/useSupplierRequests";
import { useCategoryTree } from "../categories/hooks/useCategoryTree";
import { CreateSupplierItemRequestDto } from "@semo/api-contracts";

interface SupplierItemRequestDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  initialCategoryId?: string;
}

export const SupplierItemRequestDialog: React.FC<
  SupplierItemRequestDialogProps
> = ({ isOpen, onOpenChange, initialCategoryId }) => {
  const { categoryTree } = useCategoryTree();
  const { createRequest, isCreating } = useSupplierRequests();

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSubmit = async (data: ProductFormData) => {
    // Transform ProductFormData to CreateSupplierItemRequestDto
    const requestData: CreateSupplierItemRequestDto = {
      name: data.name.trim(),
      categoryId: data.categoryId,
      unit: data.unit ?? "EA",
      manufacturer: data.manufacturer ?? "",
      brand: data.brand ?? "",
      spec1: data.spec1 ?? "",
      spec2: data.spec2 ?? "",
      spec3: data.spec3 ?? "",
      requestReason: data.requestReason ?? "",
    };

    if (!requestData.name) {
      toast.error("상품명을 입력해주세요.");
      return;
    }

    if (!requestData.categoryId) {
      toast.error("카테고리를 선택해주세요.");
      return;
    }

    try {
      await createRequest(requestData);
      handleClose();
    } catch (error) {
      console.error("Failed to submit item request:", error);
      // Error toast is handled in useSupplierRequests hook
    }
  };

  return (
    <ProductForm
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={handleSubmit}
      categories={categoryTree}
      categoryId={initialCategoryId || ""}
      categoryName="카테고리 선택"
      title="신규 물품 등록 요청"
      submitLabel={isCreating ? "제출 중..." : "요청 제출"}
      isSubmitting={isCreating}
      sections={{
        category: true,
        basicInfo: true,
        specs: true,
        pricing: false, // Vendors don't set master price
        image: false, // Current request DTO doesn't support image
        requestReason: true,
      }}
    />
  );
};
